import { morph } from '@theme/morph';

/**
 * In-place product switch.
 *
 * The weight / rack selectors on the dumbbell-set pages link to four separate
 * products. Instead of a full page load, a click swaps the page's sections for
 * the other product's — the same route Horizon takes for combined listings —
 * then updates the URL. Scroll position, header and footer are untouched, so
 * the switch reads as a variant change. Any failure falls back to a normal
 * navigation to the link, so nothing depends on this.
 *
 * Two things keep it fast:
 * - Only the sections inside <main> are fetched, through Shopify's Section
 *   Rendering API (`?sections=a,b,c`): about a quarter of the full page, and
 *   morphing per section costs a few milliseconds instead of a hundred.
 * - Once the page has settled, the other three products are fetched in the
 *   background and kept for ten minutes, so a click usually needs no network
 *   at all. Skipped when the visitor asked for reduced data.
 *
 * Judge.me renders its review widget client-side and its stars from
 * server-side markup; both are restored after the swap (see refreshJudgeMe).
 * Horizon's recommendations section hydrates itself when it connects, so that
 * section is replaced as a node rather than morphed. The gallery goes back to
 * the first photo: a different product starts on its own first image.
 */
const LINK_SELECTOR = 'a.wolv-switch__option';
const CACHE_TTL = 10 * 60 * 1000;

/** @type {Map<string, { at: number, sections: Record<string, string | null> }>} */
const cache = new Map();

/** @type {AbortController | null} */
let controller = null;

/** @type {string[] | null} */
let sectionIds = null;

/**
 * The ids of every section inside <main>; all of them belong to the product.
 * @returns {string[]}
 */
function mainSectionIds() {
  if (sectionIds) return sectionIds;
  const main = document.querySelector('main');
  sectionIds = main
    ? [...main.querySelectorAll(':scope > .shopify-section[id^="shopify-section-"]')].map((el) =>
        el.id.replace(/^shopify-section-/, ''),
      )
    : [];
  return sectionIds;
}

/**
 * @param {string} href
 * @returns {string} The Section Rendering API URL for that product page.
 */
function sectionsUrl(href) {
  const url = new URL(href, window.location.href);
  url.searchParams.set('sections', mainSectionIds().join(','));
  return url.href;
}

/**
 * @param {string} href
 * @returns {Record<string, string | null> | null} Cached sections, if still fresh.
 */
function cached(href) {
  const entry = cache.get(href);
  if (!entry) return null;
  if (Date.now() - entry.at > CACHE_TTL) {
    cache.delete(href);
    return null;
  }
  return entry.sections;
}

/**
 * Fetches the product's sections and stores them.
 * @param {string} href
 * @param {AbortSignal} [signal]
 * @returns {Promise<Record<string, string | null>>}
 */
async function load(href, signal) {
  // No Accept: application/json here: Shopify would answer a product page
  // request with the product JSON instead of the rendered sections.
  const response = await fetch(sectionsUrl(href), { signal });
  if (!response.ok) throw new Error(`Product switch: ${response.status} for ${href}`);
  const sections = /** @type {Record<string, string | null>} */ (await response.json());
  if (!sections || typeof sections !== 'object') throw new Error('Product switch: unexpected sections response');
  cache.set(href, { at: Date.now(), sections });
  return sections;
}

/**
 * Swaps every fetched section into the page.
 * @param {Record<string, string | null>} sections
 * @returns {boolean} Whether the product section was swapped.
 */
function apply(sections) {
  let swappedMain = false;
  const parser = new DOMParser();

  for (const [id, html] of Object.entries(sections)) {
    if (typeof html !== 'string') continue;
    const current = document.getElementById(`shopify-section-${id}`);
    const next = parser.parseFromString(html, 'text/html').getElementById(`shopify-section-${id}`);
    if (!current || !next) continue;

    if (next.querySelector('product-recommendations')) {
      // Loads its cards when it connects; a fresh node makes it do that again.
      const revealed = current.classList.contains('is-revealed');
      current.replaceWith(next);
      if (revealed) next.classList.add('is-revealed');
      continue;
    }

    // App blocks get classes from their app's script after load (Judge.me adds
    // jdgm-horizon-widget, which gives the wrapper its width); the fetched
    // markup carries none of those, so they are put back after the morph.
    const appBlockClasses = new Map(
      [...current.querySelectorAll('.shopify-app-block[id]')].map((el) => [el.id, [...el.classList]]),
    );

    // Children only: the wrapper keeps its own state (e.g. .is-revealed from
    // the scroll-reveal script, which the fetched markup does not carry).
    morph(current, next);

    for (const [blockId, classes] of appBlockClasses) document.getElementById(blockId)?.classList.add(...classes);
    if (current.querySelector('.product-information')) swappedMain = true;
  }

  return swappedMain;
}

/**
 * Judge.me after the swap.
 *
 * Preview badge (stars beside the eyebrow): the star markup ships server-side
 * inside a hidden .jdgm-prev-badge that Judge.me's script reveals once it has
 * run. The swapped-in badge is the new product's but carries the server-side
 * inline display:none again, so it is revealed here.
 *
 * Review widget: a Vue app that Judge.me's app-embed module mounts once per
 * .jdgm-review-widget[data-entry-point] container, keyed by element. It reads
 * the product's review data from jdgm.data.reviewWidget[id], which an inline
 * <script class="jdgm-review-widget-data"> next to the container fills. The
 * swap brings the new product's container and data script but, as with any
 * DOM update, does not execute scripts, and the module runs only once. So the
 * data script is executed again by re-inserting it, and the module is
 * imported again with a cache-busting query, which mounts a fresh app on the
 * container. Any failure here leaves the page without reviews until the next
 * full load; nothing else depends on it.
 */
function refreshJudgeMe() {
  for (const badge of document.querySelectorAll('.jdgm-prev-badge')) {
    if (badge instanceof HTMLElement && badge.style.display === 'none') badge.style.display = '';
  }
  const jdgm = /** @type {any} */ (window).jdgm;
  if (jdgm && typeof jdgm.customizeBadges === 'function') {
    try {
      jdgm.customizeBadges();
    } catch {
      // the badge is already visible; nothing else to do
    }
  }
  for (const data of document.querySelectorAll('script.jdgm-review-widget-data')) {
    const fresh = document.createElement('script');
    fresh.className = data.className;
    fresh.textContent = data.textContent;
    data.replaceWith(fresh);
  }
  const module = /** @type {HTMLScriptElement | null} */ (document.querySelector('script[src*="review_widget"]'));
  if (module?.src && document.querySelector('.jdgm-review-widget[data-entry-point]')) {
    try {
      const url = new URL(module.src);
      url.searchParams.set('wolv-switch', String(Date.now()));
      import(/* @vite-ignore */ url.href).catch(() => {});
    } catch {
      // no reviews until the next full page load
    }
  }
}

/**
 * The gallery keeps its slide index across the swap (slide 4 of the old
 * product would become slide 4 of the new one); a different product starts
 * on its first photo.
 */
function resetGallery() {
  queueMicrotask(() => {
    for (const slideshow of document.querySelectorAll('.product-information__media slideshow-component')) {
      if (typeof (/** @type {any} */ (slideshow)).select === 'function') {
        try {
          /** @type {any} */ (slideshow).select(0, undefined, { animate: false });
        } catch {
          // gallery stays where it is
        }
      }
    }
  });
}

/**
 * Fetches the other products of the switch in the background, one at a time,
 * so a later click needs no network. Runs once the page is idle.
 */
function prefetch() {
  const connection = /** @type {any} */ (navigator).connection;
  if (connection?.saveData) return;
  const hrefs = [
    ...new Set([...document.querySelectorAll(LINK_SELECTOR)].map((a) => /** @type {HTMLAnchorElement} */ (a).href)),
  ];
  (async () => {
    for (const href of hrefs) {
      if (cached(href)) continue;
      try {
        await load(href);
      } catch {
        // a later click simply fetches on demand
      }
    }
  })();
}

function schedulePrefetch() {
  const run = () => {
    if ('requestIdleCallback' in window) {
      /** @type {any} */ (window).requestIdleCallback(prefetch, { timeout: 4000 });
    } else {
      setTimeout(prefetch, 1500);
    }
  };
  if (document.readyState === 'complete') {
    setTimeout(run, 1000);
  } else {
    window.addEventListener('load', () => setTimeout(run, 1000), { once: true });
  }
}

/**
 * @param {MouseEvent} event - The click event.
 */
async function onClick(event) {
  const target = /** @type {Element} */ (event.target);
  const link = /** @type {HTMLAnchorElement | null} */ (target.closest(LINK_SELECTOR));
  if (!link || event.defaultPrevented) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

  const container = link.closest('.wolv-switch');
  if (!container || !mainSectionIds().length) return;

  // Read the target now: after the swap this anchor is the other page's link.
  const href = link.href;

  event.preventDefault();
  controller?.abort();
  controller = new AbortController();
  container.setAttribute('aria-busy', 'true');

  try {
    const sections = cached(href) ?? (await load(href, controller.signal));
    if (!apply(sections)) throw new Error('Product switch: product section missing in the response');

    resetGallery();
    history.replaceState({}, '', href);
    refreshJudgeMe();
    document.dispatchEvent(new CustomEvent('wolv:product-switched', { detail: { url: href } }));
    schedulePrefetch();
  } catch (error) {
    if (/** @type {any} */ (error)?.name === 'AbortError') return;
    console.warn(error);
    window.location.href = href;
    return;
  } finally {
    document.querySelector('.wolv-switch')?.removeAttribute('aria-busy');
  }
}

if (!/** @type {any} */ (window).__wolvProductSwitch) {
  /** @type {any} */ (window).__wolvProductSwitch = true;
  document.addEventListener('click', onClick);
  if (document.querySelector(LINK_SELECTOR)) schedulePrefetch();
}
